import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, catchError, Observable, tap, throwError } from 'rxjs';
import { AuthenticatedHttpClient } from '../../auth/authenticatedHttpClient';
import {
  DRIVER_ONBOARDING_STATUS_ORDER,
  DriverOnboardingStatus,
  Profile,
} from '../model/profile.model';
import * as Sentry from '@sentry/angular';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService, UserRole } from 'src/app/auth/auth.service';
import { BonusPercent } from 'src/app/shared/model/status-bonus.model';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  public profile: Profile;

  private apiUrl = `${environment.carmovaApiUrl}/drivers/profile`;
  private apiManagerUrl = `${environment.carmovaApiUrl}/managers/profile`;

  private _profile$: BehaviorSubject<Profile | undefined> = new BehaviorSubject<
    Profile | undefined
  >(undefined);

  public premiumPartnerLevel: number;

  // Status-bonus percentages from GET /drivers/current-ppl. null until loaded;
  // no default — while null the bonus resolves to 0 and the UI stays hidden.
  public statusBonusPercent: BonusPercent | null = null;

  public onboardingStatus: DriverOnboardingStatus;

  // Onboarding-page gate owned entirely by the backend (GET /drivers/profile →
  // `onboardingEnabled`). Defaults to true when the flag is missing from the
  // payload
  public onboardingEnabled = true;

  public onboardingSteps: DriverOnboardingStatus[] = [
    ...DRIVER_ONBOARDING_STATUS_ORDER,
  ];

  constructor(
    private readonly http: AuthenticatedHttpClient,
    private readonly authService: AuthService,
  ) {}

  get profile$(): BehaviorSubject<Profile | undefined> {
    return this._profile$;
  }

  private cacheOnboarding(driver: Profile): void {
    this.onboardingStatus = driver.onboardingStatus;
    this.onboardingEnabled = driver.onboardingEnabled ?? true;

    this.onboardingSteps = driver.onboarding?.steps?.length
      ? driver.onboarding.steps
      : [...DRIVER_ONBOARDING_STATUS_ORDER];
  }

  fetchProfile() {
    return this.http.get<Profile>(this.apiUrl).pipe(
      tap((driver) => {
        this.profile = driver;
        this.cacheOnboarding(driver);

        this._profile$.next(driver);
      }),
      catchError((error: HttpErrorResponse) => {
        Sentry.captureException(error);

        return throwError(() => new Error('movacarpro_error_message_unknown'));
      }),
    );
  }

  updateProfile(profileUpdate: Profile): Observable<Profile> {
    return this.http.put<Profile>(this.apiUrl, profileUpdate).pipe(
      tap((updatedProfile: Profile) => {
        this._profile$.next(updatedProfile);
      }),
      catchError((error: HttpErrorResponse) => {
        Sentry.captureException(error);
        this._profile$.next(this._profile$.value);

        return throwError(
          () => new Error('movacarpro_error_saving_data_failed'),
        );
      }),
    );
  }

  fetchDriverProfileById(driverId: string): Observable<Profile> {
    return this.http.get<Profile>(`${this.apiUrl}/${driverId}`).pipe(
      catchError((error: HttpErrorResponse) => {
        Sentry.captureException(error);

        return throwError(
          () => new Error('movacarpro_error_fetching_driver_data'),
        );
      }),
    );
  }

  updateDriverProfileById(
    driverId: string,
    profileUpdate: Profile,
  ): Observable<Profile> {
    return this.http
      .put<Profile>(`${this.apiUrl}/${driverId}`, profileUpdate)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          Sentry.captureException(error);

          return throwError(
            () => new Error('movacarpro_error_saving_data_failed'),
          );
        }),
      );
  }

  fetchContactProfile() {
    const contact = this.authService.userData;
    const contactRole = contact.role;

    const apiUrl =
      contactRole === UserRole.Manager ? this.apiManagerUrl : this.apiUrl;

    return this.http.get<Profile>(apiUrl).pipe(
      tap((driver) => {
        this.profile = driver;
        this.cacheOnboarding(driver);

        this.premiumPartnerLevel = driver.premiumPartnerLevel;

        this._profile$.next(driver);
      }),
      catchError((error: HttpErrorResponse) => {
        Sentry.captureException(error);

        return throwError(() => new Error('movacarpro_error_message_unknown'));
      }),
    );
  }

  updateContactProfile(profileUpdate: Profile): Observable<Profile> {
    const apiUrl =
      profileUpdate.role === UserRole.Manager
        ? this.apiManagerUrl
        : this.apiUrl;

    return this.http
      .put<Profile>(`${apiUrl}`, profileUpdate, {
        headers: { 'content-Type': 'application/json' },
      })
      .pipe(
        tap((updatedProfile: Profile) => {
          this._profile$.next(updatedProfile);
        }),
        catchError((error: HttpErrorResponse) => {
          Sentry.captureException(error);

          return throwError(
            () => new Error('movacarpro_error_saving_data_failed'),
          );
        }),
      );
  }

  updatePremiumPartnerLevelInSalesforce(
    currentPremiumPartnerLevel: number,
    newPremiumPartnerLevel: number,
  ) {
    if (currentPremiumPartnerLevel === newPremiumPartnerLevel) {
      return;
    }

    const contact = this.profile;
    contact.premiumPartnerLevel = newPremiumPartnerLevel;

    this.updateContactProfile(contact).subscribe({
      next: () => {
        this.premiumPartnerLevel = newPremiumPartnerLevel;
      },
      error: (error: Error) => {
        Sentry.captureException(error);
      },
    });
  }
}
